import { getApiUrl, getAuthHeaders, handleApiResponse } from '../config/api';

// Interfaces
export interface Policy {
  id: number;
  title: string;
  slug: string;
  content: string;
  description?: string;
  versionId: string;
  previousVersionId?: number;
  isExpired: boolean;
  isActive: boolean;
  expiredAt?: string;
  createdBy?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePolicyData {
  title: string;
  slug: string;
  content: string;
  description?: string;
  isActive?: boolean;
}

export interface UpdatePolicyData extends Partial<CreatePolicyData> {
  id: number;
}

export interface PolicyFilters {
  isActive?: boolean;
  isExpired?: boolean;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export interface PolicyListResponse {
  success: boolean;
  message: string;
  data: {
    policies: Policy[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
    };
  };
}

export interface PolicyResponse {
  success: boolean;
  message: string;
  data?: {
    policy: Policy;
    versions?: Policy[];
  };
}

class PolicyService {
  private baseUrl = '/admin/policies';

  // Get all policies with filters
  async getPolicies(filters: PolicyFilters = {}): Promise<PolicyListResponse> {
    try {
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const response = await fetch(getApiUrl(`${this.baseUrl}?${params.toString()}`), {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      return await handleApiResponse(response);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch policies');
    }
  }

  // Get policy by ID
  async getPolicyById(id: number): Promise<PolicyResponse> {
    try {
      const response = await fetch(getApiUrl(`${this.baseUrl}/${id}`), {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      return await handleApiResponse(response);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch policy');
    }
  }

  // Get policy by slug
  async getPolicyBySlug(slug: string): Promise<PolicyResponse> {
    try {
      const response = await fetch(getApiUrl(`${this.baseUrl}/slug/${slug}`), {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      return await handleApiResponse(response);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch policy');
    }
  }

  // Get policy version history
  async getPolicyVersions(slug: string): Promise<PolicyResponse> {
    try {
      const response = await fetch(getApiUrl(`${this.baseUrl}/versions/${slug}`), {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      return await handleApiResponse(response);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch policy versions');
    }
  }

  // Create policy
  async createPolicy(policyData: CreatePolicyData): Promise<PolicyResponse> {
    try {
      const response = await fetch(getApiUrl(this.baseUrl), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(policyData),
      });
      return await handleApiResponse(response);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to create policy');
    }
  }

  // Update policy (creates new version)
  async updatePolicy(id: number, policyData: Partial<CreatePolicyData>): Promise<PolicyResponse> {
    try {
      const response = await fetch(getApiUrl(`${this.baseUrl}/${id}`), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(policyData),
      });
      return await handleApiResponse(response);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to update policy');
    }
  }

  // Toggle policy status
  async togglePolicyStatus(id: number, isActive: boolean): Promise<PolicyResponse> {
    try {
      const response = await fetch(getApiUrl(`${this.baseUrl}/${id}/toggle-status`), {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ isActive }),
      });
      return await handleApiResponse(response);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to toggle policy status');
    }
  }

  // Delete policy
  async deletePolicy(id: number): Promise<PolicyResponse> {
    try {
      const response = await fetch(getApiUrl(`${this.baseUrl}/${id}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      return await handleApiResponse(response);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to delete policy');
    }
  }
}

export const policyService = new PolicyService();
